import { apiDocuments } from "@/api/document.api.ts";
import { apiFiles } from "@/api/file.api.ts";
import { employeeDocumentsKeys } from "@/queries/employees/use-employee-documents.query.ts";
import { documentsKeys } from "@/queries/use-documents.query.ts";
import { ApiDocumentSavePayload } from "@/types/document.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

export type BulkUploadStatus = "pending" | "uploading" | "done" | "error";

export interface BulkUploadItem {
  id: string;
  name: string;
  status: BulkUploadStatus;
}

export const useDocumentBulkUpload = ({
  folderId,
  employeeId,
}: {
  folderId: number;
  employeeId: number | null;
}) => {
  const [queue, setQueue] = useState<BulkUploadItem[]>([]);
  const queryClient = useQueryClient();
  const drainingRef = useRef(false);
  const pendingRef = useRef<Array<{ id: string; file: File }>>([]);

  const drain = useCallback(async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;

    while (pendingRef.current.length > 0) {
      const next = pendingRef.current.shift()!;
      const { id, file } = next;

      setQueue((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status: "uploading" } : item))
      );

      const formData = new FormData();
      formData.append("file", file);
      formData.append(
        "path",
        employeeId !== null ? ApiFilePathTypes.documents : ApiFilePathTypes.organization_documents
      );
      formData.append("is_public", "false");
      formData.append("save_original_name", "true");
      if (employeeId !== null) formData.append("entity_id", employeeId.toString());

      const { data: uploadedFile } = await apiFiles.add(formData);

      if (!uploadedFile) {
        setQueue((prev) =>
          prev.map((item) => (item.id === id ? { ...item, status: "error" } : item))
        );
        continue;
      }

      const payload: ApiDocumentSavePayload = {
        title: file.name,
        folder_id: folderId,
        file_uuid: uploadedFile.uuid,
      };

      try {
        if (employeeId !== null) {
          await apiDocuments.createDocumentForEmployee(employeeId, payload);
        } else {
          await apiDocuments.createDocumentForOrganization(payload);
        }
        setQueue((prev) =>
          prev.map((item) => (item.id === id ? { ...item, status: "done" } : item))
        );
        void queryClient.invalidateQueries({ queryKey: documentsKeys.search._def });
        void queryClient.invalidateQueries({ queryKey: documentsKeys.folders._def });
        void queryClient.invalidateQueries({ queryKey: employeeDocumentsKeys.folders._def });
      } catch {
        setQueue((prev) =>
          prev.map((item) => (item.id === id ? { ...item, status: "error" } : item))
        );
      }
    }

    drainingRef.current = false;
  }, [folderId, employeeId, queryClient]);

  const addFiles = useCallback(
    (files: File[]) => {
      const items = files.map((file) => ({ id: crypto.randomUUID(), file }));
      setQueue((prev) => [
        ...prev,
        ...items.map(({ id, file }) => ({ id, name: file.name, status: "pending" as const })),
      ]);
      pendingRef.current.push(...items);
      void drain();
    },
    [drain]
  );

  const clearQueue = useCallback(() => {
    setQueue([]);
  }, []);

  const isUploading = queue.some(
    (item) => item.status === "uploading" || item.status === "pending"
  );

  return { queue, addFiles, clearQueue, isUploading };
};
